import { find, isEmpty } from 'lodash'
import { CONFIG } from '../../Config/config'
import { authorization } from '../../Module/Auth/Auth'

/**
 * API client to communicate with OPPIZI API
 */
class APIClient {
  /**
   * API Path
   * @type Object
   */
  path = {
    signFileUpload: '/sign-file-upload',
    ambassadors: {
      getAmbassadors: '/ambassadors',
      inventory: '/inventory',
      signDocumentUpload: 'sign-document-upload',
    },
    user: {
      auth: '/auth',
      authOAuth2: '/auth/oauth2',
      logout: '/logout',
      disconnectOAuth2Provider: '/disconnect',
      requestResetPassword: '/request-forgot-password',
      signProfileUpload: '/sign-profile-upload',
      codeAndHashValidation: '/reset-password?hash={#hash}&code={#code}',
      resetPassword: '/reset-password',
      getUsers: '/users',
      getUserById: '/users',
      createUser: '/users',
      updateUserById: '/users',
      signFileUpload: '/sign-file-upload',
    },
    clients: {
      createClient: '/clients',
      getClients: '/clients',
      updateClients: '/clients',
      signClientUpload: '/sign-client-upload',
    },
    campaigns: {
      signPitchUpload: '/sign-pitch-upload',
      getBonusResults: 'bonus-results',
      getCampaigns: '/campaigns',
      getMissions: '/missions',
      createCampaign: '/campaigns',
      cityPlans: '/city-plans',
      getPayments: '/payments',
      signConversionReportUpload: 'sign-report-upload',
      reports: '/reports',
      insertsCostMestrics: '/campaigns/inserts/cost-metrics',
    },
    city: {
      signPitchUpload: '/sign-pitch-upload',
      getCities: '/cities',
    },
    inventory: {
      inventory: '/inventory',
      inventoryTypes: '/inventory-items',
    },
    locations: {
      locations: '/locations',
      locationTypes: '/location-types',
      locationsByCampaign: '/locations-by-campaign',
    },
    missions: {
      getMissions: '/missions',
      deleteMission: '/missions',
      getActivities: '/activities',
      publishMissions: '/missions/publish',
    },
    rosters: {
      getRosters: '/rosters',
      deleteRoster: '/rosters',
      createRoster: '/rosters',
    },
    countries: {
      getCountries: '/countries',
    },
    pickupTimes: {
      getTimes: '/pickup-time',
      save: '/pickup-time',
    },
    document: {
      documentTypes: '/document-types',
    },
    xero: {
      exportedXeros: '/exports',
    },
    jobs: {
      getJobs: '/jobs',
    },
    chat: {
      getContacts: '/contacts',
    },
    metrics: {
      getMetrics: '/metric',
      byCampaign: '/campaign',
    },
    globalSearch: '/global-search',
    referralProgram: {
      exportCSV: '/referral-export',
    },
    promoProgram: {
      exportCSV: '/promo-export',
    },
    signLegalDocumentUpload: 'sign-legal-document-upload',
  }

  /**
   * GET ambassadors by city id
   */
  getAmbassadorsByCityId = cityId => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${
        this.path.ambassadors.getAmbassadors
      }?city_id=${cityId}`,
      'GET',
    )
    return fetch(request)
  }

  /**
   * POST upload ambassador document
   */
  uploadAmbassadorDocument = (ambassadorId, body) => {
    return this.signAmbassadorDocument(ambassadorId, { filename: body.name })
      .then(resp => resp.json())
      .then(data => this.s3UploadRequest(body, data))
  }

  /**
   * POST sign ambassador document
   */
  signAmbassadorDocument = (ambassadorId, payload) => {
    const {
      ambassadors: { getAmbassadors, signDocumentUpload },
    } = this.path
    const request = this.prepareRequest(
      `${
        CONFIG.API_BASE_URL
      }${getAmbassadors}/${ambassadorId}/${signDocumentUpload}`,
      'POST',
      payload,
    )
    return fetch(request)
  }

  /**
   * Edit inventory
   */
  editInventory = (inventoryId, payload) => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${
        this.path.inventory.inventoryTypes
      }/${inventoryId}`,
      'PUT',
      payload,
    )
    return fetch(request)
  }

  /**
   * GET ambassador by id
   *
   * @param ambassadorId
   * @returns {Promise<Response>}
   */
  getAmbassadorById = ambassadorId => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${
        this.path.ambassadors.getAmbassadors
      }/${ambassadorId}`,
      'GET',
    )
    return fetch(request)
  }

  /**
   * UPDATE ambassador by id
   */
  updateAmbassadorById = (ambassadorId, payload) => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${
        this.path.ambassadors.getAmbassadors
      }/${ambassadorId}`,
      'PUT',
      payload,
    )
    return fetch(request)
  }

  /**
   * GET inventory types
   *
   * @returns {Promise<Response>}
   */
  getInventoryTypes = () => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.inventory.inventoryTypes}`,
      'GET',
    )
    return fetch(request)
  }

  /**
   * GET ambassador documents by id
   */
  getAmbassadorDocumentsById = ambassadorId => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${
        this.path.ambassadors.getAmbassadors
      }/${ambassadorId}/documents`,
      'GET',
    )
    return fetch(request)
  }

  /**
   * POST ambassador documents by id
   */
  createAmbassadorDocumentsById = (ambassadorId, payload) => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${
        this.path.ambassadors.getAmbassadors
      }/${ambassadorId}/documents`,
      'POST',
      payload,
    )
    return fetch(request)
  }

  /**
   * GET ambassador activities
   */
  getAmbassadorActivitiesById = ambassadorId => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${
        this.path.ambassadors.getAmbassadors
      }/${ambassadorId}/missions`,
      'GET',
    )
    return fetch(request)
  }
  /**
   * SET ambassador status
   */
  setAmbassadorStatus = (ambassadorId, status) => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${
        this.path.ambassadors.getAmbassadors
      }/${ambassadorId}`,
      'PUT',
      { status },
    )
    return fetch(request)
  }

  /**
   * POST create new city plan
   *
   * @param body
   * @param campaignId
   * @returns {Promise<Response>}
   */
  createCityPlan = (body, campaignId) => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.campaigns.getCampaigns}/${campaignId}${
        this.path.campaigns.cityPlans
      }`,
      'POST',
      body,
    )
    return fetch(request)
  }

  /**
   * PUT edit a city plan
   *
   * @param body
   * @param cityId
   * @returns {Promise<Response>}
   */
  editCityPlan = (body, cityId) => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.campaigns.cityPlans}/${cityId}`,
      'PUT',
      body,
    )
    return fetch(request)
  }

  /**
   * DELETE city plan by id
   *
   * @param cityPlanId
   * @returns {Promise<Response>}
   */
  deleteCityPlanById = cityPlanId => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.campaigns.cityPlans}/${cityPlanId}`,
      'DELETE',
    )
    return fetch(request)
  }

  /**
   * GET city plan by id
   *
   * @param cityPlanId
   * @returns {Promise<Response>}
   */
  getCityPlanById = cityPlanId => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.campaigns.cityPlans}/${cityPlanId}`,
      'GET',
    )
    return fetch(request)
  }

  /**
   * GET city plans by campaign id
   *
   * @param campaignId
   * @returns {Promise<Response>}
   */
  getCityPlansByCampaignId = campaignId => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.campaigns.getCampaigns}/${campaignId}${
        this.path.campaigns.cityPlans
      }`,
      'GET',
    )
    return fetch(request)
  }

  /**
   * GET clients
   */
  getClients = () => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.clients.getClients}`,
      'GET',
    )
    return fetch(request)
  }

  /**
   * Get client by id
   *
   * @param clientId
   * @returns {Promise<Response>}
   */
  getClientById = clientId => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.clients.getClients}/${clientId}`,
      'GET',
    )
    return fetch(request)
  }

  /**
   * PUT client by id
   *
   * @param clientId
   * @param payload
   * @returns {Promise<Response>}
   */
  updateClientById = (clientId, payload) => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.clients.updateClients}/${clientId}`,
      'PUT',
      payload,
    )
    return fetch(request)
  }

  /**
   * POST create client
   *
   * @param payload
   * @returns {Promise<Response>}
   */
  createClient = payload => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.clients.createClient}`,
      'POST',
      payload,
    )
    return fetch(request)
  }

  /**
   * PUT update mission
   *
   * @param body
   * @returns {Promise<Response>}
   */
  updateMission = (missionId, body) => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.campaigns.getMissions}/${missionId}`,
      'PUT',
      body,
    )
    return fetch(request)
  }

  createMissionPickup = (missionId, body) => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${
        this.path.campaigns.getMissions
      }/${missionId}/pickup`,
      'POST',
      body,
    )
    return fetch(request)
  }

  /**
   * GET Get missions to pickup
   *
   * @param body
   * @returns {Promise<Response>}
   */
  missiontoPickUp = (missionId, body) => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${
        this.path.campaigns.getMissions
      }/${missionId}/pickup`,
      'GET',
      body,
    )
    return fetch(request)
  }

  /**
   * POST create campaign
   *
   * @param body
   * @returns {Promise<Response>}
   */
  createCampaign = body => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.campaigns.createCampaign}`,
      'POST',
      body,
    )
    return fetch(request)
  }

  /**
   * GET campaigns
   */
  getCampaigns = () => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.campaigns.getCampaigns}`,
      'GET',
    )
    return fetch(request)
  }

  /**
   * GET campaigns by client id
   */
  getCampaignsByClientId = clientId => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${
        this.path.campaigns.getCampaigns
      }?client_id=${clientId}`,
      'GET',
    )
    return fetch(request)
  }

  /**
   * POST upload conversion report
   *
   * @param campaignId
   * @param body
   */
  uploadConversionReport = (campaignId, body, onProgress) => {
    const filename = { filename: body.name }
    return this.signConversionReport(campaignId, filename)
      .then(response => response.json())
      .then(data => this.s3UploadRequest(body, data, onProgress))
  }

  /**
   * POST sign conversion report
   *
   * @param campaignId
   * @param body
   * @returns {Promise<Response>}
   */
  signConversionReport = (campaignId, body) => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${
        this.path.campaigns.getCampaigns
      }/${campaignId}/${this.path.campaigns.signConversionReportUpload}`,
      'POST',
      body,
    )

    return fetch(request)
  }

  /**
   * POST add conversion report
   *
   * @param campaignId
   * @param body
   * @returns {Promise<Response>}
   */
  addConversionReport = (campaignId, body) => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.campaigns.getCampaigns}/${campaignId}${
        this.path.campaigns.reports
      }`,
      'POST',
      body,
    )

    return fetch(request)
  }

  getBonusResults = campaignId => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${
        this.path.campaigns.getCampaigns
      }/${campaignId}/${this.path.campaigns.getBonusResults}`,
    )
    return fetch(request)
  }
  /**
   * Get cities
   * @returns {Promise<Response>}
   */
  getCities = includesInactive => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${
        this.path.city.getCities
      }?includes_inactive=${includesInactive}`,
      'GET',
    )
    return fetch(request)
  }

  /**
   * GET city by id
   *
   * @param cityId
   * @returns {Promise<Response>}
   */
  getCityById = cityId => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.city.getCities}/${cityId}`,
      'GET',
    )
    return fetch(request)
  }

  /**
   * POST city
   *
   * @param payload
   * @returns {Promise<Response>}
   */
  createCity = payload => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.city.getCities}`,
      'POST',
      payload,
    )
    return fetch(request)
  }

  /**
   * PUT city by id
   *
   * @param cityId
   * @param payload
   * @returns {Promise<Response>}
   */
  updateCityById = (cityId, payload) => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.city.getCities}/${cityId}`,
      'PUT',
      payload,
    )
    return fetch(request)
  }

  /**
   * GET city missions
   *
   * @param cityId
   * @returns {Promise<Response>}
   */
  getCityMissions = cityId => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.city.getCities}/${cityId}/missions`,
      'GET',
    )
    return fetch(request)
  }

  getScheduledPickups = cityId => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${
        this.path.city.getCities
      }/${cityId}/scheduled-pickups`,
      'GET',
    )
    return fetch(request)
  }

  /**
   * GET city inventories
   *
   * @param cityId
   * @returns {Promise<Response>}
   */
  getCityInventories = cityId => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.city.getCities}/${cityId}/inventory`,
      'GET',
    )
    return fetch(request)
  }

  /**
   * GET city pickup time
   *
   * @param cityId
   * @returns {Promise<Response>}
   */
  getCityPickupTime = cityId => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.city.getCities}/${cityId}/pickup-time`,
      'GET',
    )
    return fetch(request)
  }

  /**
   * POST city pickup time
   *
   * @param cityId
   * @param payload
   * @returns {Promise<Response>}
   */
  createCityPickupTime = (cityId, payload) => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.city.getCities}/${cityId}/pickup-time`,
      'POST',
      payload,
    )
    return fetch(request)
  }

  /**
   * POST city bad weather surges
   *
   * @param cityId
   * @param payload
   * @returns {Promise<Response>}
   */
  createCityBadWeatherSurges = (cityId, payload) => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.city.getCities}/${cityId}/bad-weather`,
      'POST',
      payload,
    )
    return fetch(request).then(res => res.json())
  }
  /**
   * GET city bad weather surges
   *
   * @param cityId
   * @returns {Promise<Response>}
   */
  getCityBadWeatherSurges = cityId => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.city.getCities}/${cityId}/bad-weather`,
      'GET',
    )
    return fetch(request).then(res => res.json())
  }
  /**
   * GET location by id
   *
   * @param id
   * @returns {Promise<Response>}
   */
  getLocationById = id => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.locations.locations}/${id}`,
      'GET',
    )
    return fetch(request)
  }

  /**
   * GET location by campaignId
   *
   * @param campaignId
   * @returns {Promise<Response>}
   */
  getLocationByCampaignId = campaignId => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${
        this.path.locations.locationsByCampaign
      }/${campaignId}`,
      'GET',
    )
    return fetch(request)
  }

  /**
   * PUT location by id
   *
   * @param id
   * @returns {Promise<Response>}
   */
  updateLocationById = (id, body) => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.locations.locations}/${id}`,
      'PUT',
      body,
    )
    return fetch(request)
  }

  /**
   * POST create location
   *
   * @param body
   * @returns {Promise<Response>}
   */
  createLocation = body => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.locations.locations}`,
      'POST',
      body,
    )
    return fetch(request)
  }

  /**
   * GET locations by city id
   *
   * @param cityId
   * @returns {Promise<Response>}
   */
  getLocationsByCityId = (cityId, includeInactive) => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.city.getCities}/${cityId}${
        this.path.locations.locations
      }?includes_inactive=${includeInactive}`,
      'GET',
    )
    return fetch(request)
  }

  /**
   * GET location types
   *
   * @returns {Promise<Response>}
   */
  getLocationTypes = () => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.locations.locationTypes}`,
      'GET',
    )
    return fetch(request)
  }

  /**
   * POST archive location by location id
   *
   * @param id
   */
  archiveLocation = id => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.locations.locations}/${id}/archive`,
      'POST',
    )
    return fetch(request)
  }

  /**
   * GET missions page by city id
   *
   * @param cityId
   * @param payload
   * @returns {Promise<Response>}
   */
  getMissionsPageByCityId = (cityId, payload) => {
    const queryString = () => {
      const { rowsPerPage, page, filters } = payload
      const str = `?rowsPerPage=${rowsPerPage}&page=${page}${
        isEmpty(filters) ? '' : `&filters=${JSON.stringify(filters)}`
      }`
      return str
    }
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.city.getCities}/${cityId}${
        this.path.missions.getMissions
      }${queryString()}`,
      'GET',
    )
    return fetch(request)
  }

  /**
   * GET rosters
   *
   * @returns {Promise<Response>}
   */
  getRosters = () => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.rosters.getRosters}`,
      'GET',
    )
    return fetch(request)
  }

  /**
   * GET roster by id
   *
   * @param rosterId
   */
  getRosterById = rosterId => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.rosters.getRosters}/${rosterId}`,
      'GET',
    )
    return fetch(request)
  }

  /**
   * GET rosters by city plan id
   *
   * @param cityPlanId
   * @returns {Promise<Response>}
   */
  getRostersByCityPlanId = cityPlanId => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.campaigns.cityPlans}/${cityPlanId}${
        this.path.rosters.getRosters
      }`,
      'GET',
    )
    return fetch(request)
  }

  /**
   * GET rosters by city plan id
   *
   * @param cityPlanId
   * @returns {Promise<Response>}
   */
  getRostersByCityId = cityId => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.city.getCities}/${cityId}${
        this.path.rosters.getRosters
      }`,
      'GET',
    )
    return fetch(request)
  }

  /**
   * DELETE roster by id
   *
   * @param rosterId
   * @returns {Promise<Response>}
   */
  deleteRosterById = rosterId => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.rosters.deleteRoster}/${rosterId}`,
      'DELETE',
    )
    return fetch(request)
  }

  /**
   * Get missions
   *
   * @param campaignId
   * @returns {Promise<Response>}
   */
  getCampaignMissions = campaignId => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.campaigns.getCampaigns}/${campaignId}${
        this.path.missions.getMissions
      }`,
      'GET',
    )
    return fetch(request)
  }

  /**
   * Get pickup times
   *
   * @param cityId
   * @returns {Promise<Response>}
   */
  getPickupTimes = cityId => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.city.getCities}/${cityId}${
        this.path.pickupTimes.getTimes
      }`,
      'GET',
    )
    return fetch(request)
  }

  /**
   * Save pickup times
   *
   * @param cityId
   * @param body
   * @returns {Promise<Response>}
   */
  savePickupTimes = (cityId, body) => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.city.getCities}/${cityId}${
        this.path.pickupTimes.save
      }`,
      'POST',
      body,
    )
    return fetch(request)
  }

  /**
   * GET mission by id
   */
  getMissionById = missionId => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.campaigns.getMissions}/${missionId}`,
      'GET',
    )
    return fetch(request)
  }

  /**
   * DELETE mission by id
   *
   * @param missionId
   * @returns {Promise<Response>}
   */
  deleteMissionById = missionId => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.missions.deleteMission}/${missionId}`,
      'DELETE',
    )
    return fetch(request)
  }

  /**
   * POST payments by campaign id
   *
   * @param campaignId
   * @param body
   * @returns {Promise<Response>}
   */
  editPaymentsByCampaignId = (campaignId, body) => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.campaigns.getCampaigns}/${campaignId}${
        this.path.campaigns.getPayments
      }`,
      'POST',
      body,
    )
    return fetch(request)
  }

  /**
   * POST create new roster
   *
   * @param body
   * @param cityPlanId
   * @returns {Promise<Response>}
   */
  createRoster = (body, cityPlanId) => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.campaigns.cityPlans}/${cityPlanId}${
        this.path.rosters.createRoster
      }`,
      'POST',
      body,
    )
    return fetch(request)
  }

  /**
   * PUT update roster
   *
   * @param body
   * @param rosterId
   * @returns {Promise<Response>}
   */
  updateRoster = (body, rosterId) => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.rosters.getRosters}/${rosterId}`,
      'PUT',
      body,
    )
    return fetch(request)
  }

  /**
   * Login user
   * @param body
   */
  userAuth = body => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.user.auth}`,
      'POST',
      body,
    )
    return fetch(request)
  }

  /**
   * Request for reset password
   *
   * @param {Object} body
   * @returns {Promise<Response>}
   */
  userRequestResetPassword = body => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.user.requestResetPassword}`,
      'POST',
      body,
    )
    return fetch(request)
  }

  /**
   * Check code and hash validation
   *
   * @param {string} code
   * @param {string} hash
   * @returns {Promise<Response>}
   */
  userCheckCodeAndHash = (code, hash) => {
    const url = `${CONFIG.API_BASE_URL}${this.path.user.codeAndHashValidation}`
      .replace('{#hash}', hash)
      .replace('{#code}', code)
    const request = this.prepareRequest(url)
    return fetch(request)
  }

  /**
   * Set new password
   *
   * @param {Object} body
   * @returns {Promise<Response>}
   */
  userResetPassword = body => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.user.resetPassword}`,
      'POST',
      body,
    )
    return fetch(request)
  }

  /**
   * Get user by id
   *
   * @returns {Promise<Response>}
   */
  getUserById = userId => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.user.getUserById}/${userId}`,
      'GET',
    )
    return fetch(request)
  }

  /**
   * Login user using OAuth2
   * @param body
   */
  userOAuth2Auth = body => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.user.authOAuth2}`,
      'POST',
      body,
      {},
      {},
      false,
    )
    return fetch(request)
  }

  /**
   * Logout user from platform
   */
  logout = () => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.user.logout}`,
      'POST',
      null,
      {},
      {},
      false,
    )
    return fetch(request)
  }

  /**
   * Disconnect user from OAuth2 provider
   */
  disconnectOAuth2Provider = body => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}/auth/${body.providerType}${
        this.path.user.disconnectOAuth2Provider
      }`,
      'POST',
      body,
    )
    return fetch(request)
  }

  /**
   * Create user
   *
   * @param {Object} body
   * @returns {Promise<Response>}
   */
  createUser = body => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.user.createUser}`,
      'POST',
      body,
    )
    return fetch(request)
  }

  /**
   * Update user
   *
   * @param userId
   * @param {Object} body
   * @returns {Promise<Response>}
   */
  updateUserById = (userId, body) => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.user.updateUserById}/${userId}`,
      'PUT',
      body,
    )
    return fetch(request)
  }

  /**
   * GET countries
   */
  getCountries = () => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.countries.getCountries}`,
      'GET',
    )
    return fetch(request)
  }

  /**
   * Get users
   *
   * @returns {Promise<Response>}
   */
  getUsers = () => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.user.getUsers}`,
      'GET',
    )
    return fetch(request)
  }

  /**
   * GET document types
   *
   * @returns {Promise<Response>}
   */
  getDocumentTypes = () => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.document.documentTypes}`,
      'GET',
    )
    return fetch(request)
  }

  /**
   * GET document type by id
   *
   * @param id
   * @returns {Promise<Response>}
   */
  getDocumentTypeById = id => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.document.documentTypes}/${id}`,
      'GET',
    )
    return fetch(request)
  }

  /**
   * Update document type by id
   *
   * @param id
   * @param body
   * @returns {Promise<Response>}
   */
  updateDocumentTypeById = (id, body) => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.document.documentTypes}/${id}`,
      'PUT',
      body,
    )
    return fetch(request)
  }

  uploadProfileImage = (userId, body) => {
    return this.signProfileUpload(userId, { filename: body.name })
      .then(resp => resp.json())
      .then(data => this.s3UploadRequest(body, data))
  }

  /**
   * Sign profile upload
   *
   * @param body
   * @param {Object} body
   * @returns {Promise<Response>}
   */
  signProfileUpload = (userId, body) => {
    const { getUsers, signProfileUpload } = this.path.user
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${getUsers}/${userId}${signProfileUpload}`,
      'POST',
      body,
    )
    return fetch(request)
  }

  uploadClientImage = (clientId, body) => {
    return this.signClientUpload(clientId, { filename: body.name })
      .then(resp => resp.json())
      .then(data => this.s3UploadRequest(body, data))
  }

  /**
   * Sign client logo upload
   *
   * @param body
   * @param {Object} body
   * @returns {Promise<Response>}
   */
  signClientUpload = (clientId, body) => {
    const { getClients, signClientUpload } = this.path.clients
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${getClients}/${clientId}${signClientUpload}`,
      'POST',
      body,
    )
    return fetch(request)
  }

  /**
   * Upload the pitch file for a user
   * @param {string} clientId
   * @param {object} body
   */
  uploadPitchFile = (campaignId, body) => {
    const filename = body.name.replace(/ /g, '_')
    return this.signPitchUpload(campaignId, { filename })
      .then(resp => resp.json())
      .then(data => this.s3UploadRequest(body, data))
  }

  /**
   * Sign pitch file upload
   * @param body
   * @param {Object} body
   * @returns {Promise<Response>}
   */
  signPitchUpload = (campaignId, body) => {
    const { getCampaigns, signPitchUpload } = this.path.campaigns
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${getCampaigns}/${campaignId}${signPitchUpload}`,
      'POST',
      body,
    )
    return fetch(request)
  }

  /**
   * Upload the quiz pitch file for a city
   * @param {string} cityId
   * @param {object} body
   */
  uploadQuizPitchFile = async (cityId, body) => {
    return this.signQuizPitchUpload(cityId, {
      filename: body.name.replace(/ /g, '_'),
    })
      .then(resp => resp.json())
      .then(data => this.s3UploadRequest(body, data))
  }

  /**
   * Sign quiz pitch file upload
   * @param body
   * @param {Object} body
   * @returns {Promise<Response>}
   */
  signQuizPitchUpload = async (cityId, body) => {
    const { getCities, signPitchUpload } = this.path.city
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${getCities}/${cityId}${signPitchUpload}`,
      'POST',
      body,
    )
    return fetch(request)
  }

  /**
   * POST create document type
   *
   * @returns {Promise<Response>}
   */
  createDocumentType = body => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.document.documentTypes}`,
      'POST',
      body,
    )
    return fetch(request)
  }

  /**
   * POST upload a file
   */
  uploadFile = (body, callbacks, mimeType) => {
    return this.signFile({ filename: body.name })
      .then(resp => {
        if (resp.status !== 200)
          throw new Error(
            `Response was not successfully, received error code ${
              resp.status
            }.`,
          )
        return resp.json()
      })
      .then(data => this.s3UploadRequest(body, data, null, mimeType))
  }

  /**
   * POST sign ambassador document
   */
  signFile = payload => {
    const {
      user: { getUsers, signFileUpload },
    } = this.path
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${getUsers}${signFileUpload}`,
      'POST',
      payload,
    )
    return fetch(request)
  }

  signFileUpload = payload => {
    const { signFileUpload } = this.path
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${signFileUpload}`,
      'POST',
      payload,
    )
    return fetch(request)
  }

  signFileAndUploadToS3 = ({ body, contentType, type, payload }) => {
    return this.signFileUpload({ filename: body.name, type, payload })
      .then(resp => {
        if (resp.status !== 200)
          throw new Error(
            `Response was not successfully, received error code ${
              resp.status
            }.`,
          )
        return resp.json()
      })
      .then(data => this.s3UploadRequest(body, data, null, contentType))
  }

  /**
   * S3 upload
   *
   * @param {Object} file
   * @param {Object} sign
   * @returns {Promise<Response>}
   */
  s3UploadRequest = async (
    file,
    { url, key, date, policy, signature, credentials, clearPolicy },
    onProgress,
    contentType,
  ) => {
    const { acl } = find(clearPolicy.conditions, v => v.acl) || {}
    const { 'x-amz-algorithm': algorithm = 'AWS4-HMAC-SHA256' } =
      find(clearPolicy.conditions, v => v['x-amz-algorithm']) || {}

    /*var data = await getBase64(file)
    console.log(data)
    debugger*/

    /*return fetch(url, {
      method: 'post',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        key,
        acl,
        'Policy': policy,
        'X-Amz-Credential': credentials,
        'X-Amz-Algorithm': algorithm,
        'X-Amz-Date': date,
        'X-Amz-Signature': signature,
        'Content-Type': file.type,
        'file': data,
        //formData.append('success_action_redirect', window.location.href)
        //formData.append('success_action_status', '200'
      }),
    })
      .then(() => `${url}/${key}`)*/

    var formData = new FormData()

    formData.append('key', key)
    formData.append('acl', acl)
    formData.append('Policy', policy)
    formData.append('X-Amz-Credential', credentials)
    formData.append('X-Amz-Algorithm', algorithm)
    formData.append('X-Amz-Date', date)
    formData.append('X-Amz-Signature', signature)
    formData.append('Content-Type', contentType || file.type)
    formData.append('file', file)
    //formData.append('success_action_redirect', window.location.href)
    //formData.append('success_action_status', '200')
    return new Promise((res, rej) => {
      var xhr = new XMLHttpRequest()
      xhr.open('post', url)
      xhr.onload = e => res(e.target.responseText)
      // Because of CORS access if we don't get a 200 status back it will error
      // and I can't seem to get it to return a 200 instead of a 204 - no content
      // so going to assume success, because life's to short.
      xhr.onerror = res
      if (xhr.upload && onProgress) xhr.upload.onprogress = onProgress
      xhr.send(formData)
    }).then(() => `${url}/${key}`)
  }

  /*
   * GET exported xeros
   *
   * @returns {Promise<Response>}
   */
  getExportedXeros = body => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.xero.exportedXeros}`,
      'GET',
    )
    return fetch(request)
  }

  /**
   * POST Create a job
   */
  createJob = payload => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.jobs.getJobs}`,
      'POST',
      payload,
    )
    return fetch(request)
  }

  /**
   * Get campaign metrics
   */
  getCampaignMetrics = campaignId => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.metrics.getMetrics}${
        this.path.metrics.byCampaign
      }/${campaignId}`,
      'GET',
    )
    return fetch(request)
  }

  fetchContacts = () => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.chat.getContacts}`,
      'GET',
    )
    return fetch(request)
  }

  /**
   * Update Inserts cost metrics data
   */
  updateInsertsCostMetrics = body => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.campaigns.insertsCostMestrics}`,
      'POST',
      body,
    )
    return fetch(request)
  }

  /**
   * Get Inserts cost metrics data
   */
  getInsertsCostMetrics = campaignId => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${
        this.path.campaigns.insertsCostMestrics
      }/${campaignId}`,
      'GET',
    )
    return fetch(request)
  }

  /**
   * Get campaign tracking data
   */
  getInsertsCampaignTrackingData = campaignId => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}/campaigns/${campaignId}/inserts/codes/export`,
      'GET',
    )
    return fetch(request)
  }

  /**
   * Global search
   */
  globalSearch = searchQuery => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.globalSearch}?search=${searchQuery}`,
      'GET',
    )
    return fetch(request)
  }
  /**
   * GET City's referall programs csv file
   */
  getURLOfReferralProgramsCSV = cityId => {
    return `${CONFIG.API_BASE_URL}${
      this.path.referralProgram.exportCSV
    }/${cityId}?apiKey=${authorization.getAPIKey()}`
  }

  /**
   * GET City's promo programs csv file
   */
  getURLOfPromoProgramsCSV = cityId => {
    return `${CONFIG.API_BASE_URL}${
      this.path.promoProgram.exportCSV
    }/${cityId}?apiKey=${authorization.getAPIKey()}`
  }

  /**
   * POST Create missions
   */
  createMissions = missions => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.missions.getMissions}`,
      'POST',
      missions,
    )
    return fetch(request)
  }

  /**
   * PUT Publish missions
   */
  publishMissions = missions => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.missions.publishMissions}`,
      'PUT',
      missions,
    )
    return fetch(request)
  }

  /**
   * DELETE Update missions
   */
  deleteMissions = payload => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}${this.path.missions.getMissions}`,
      'DELETE',
      payload,
    )
    return fetch(request)
  }

  /**
   * Get flyers by user ID
   */
  getAvailableFlyerCodesByUserId = userId => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}/user/${userId}/flyer-codes`,
      'GET',
    )
    return fetch(request)
  }

  /**
   * POST upload ambassador document
   */
  uploadLegalDocument = (file, countryId) => {
    return this.signLegalDocument({
      filename: file.name,
      countryId,
    })
      .then(resp => resp.json())
      .then(data => this.s3UploadRequest(file, data))
  }

  /**
   * POST sign legal document
   */
  signLegalDocument = payload => {
    const { signLegalDocumentUpload } = this.path

    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}/${signLegalDocumentUpload}`,
      'POST',
      payload,
    )
    return fetch(request)
  }

  /**
   * GET  Get brand ambassador fade value
   *
   * @param userId
   * @returns {Promise<Response>}
   */
  getFadeValue = userId => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}//user/${userId}/fade`,
      'GET',
    )
    return fetch(request).then(resp => resp.json())
  }

  /**
   * PUT Change brand ambassador fade value
   *
   * @param body
   * @param campaignId
   * @returns {Promise<Response>}
   */
  setFadeValue = (userId, shouldSetFade) => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}/user/${userId}/fade`,
      'PUT',
      { fade: shouldSetFade },
    )
    return fetch(request).then(resp => resp.json())
  }

  getAndCreateShortUrls = data => {
    const request = this.prepareRequest(
      CONFIG.API_BASE_URL.replace('/platform', '/create-short-url'),
      'POST',
      {
        linksData: data,
      },
    )
    return fetchRetry(request, 3000, 10)
  }

  importUsers = (data, cityId) => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}/ambassadors/import-users`,
      'POST',
      {
        cityId,
        userData: data,
      },
    )
    return fetch(request)
  }

  /**
   * POST upload chat file
   */
  uploadChatFile = ({ file }) => {
    return this.uploadFile(file)
  }

  capturPayment = ({ paymentIntent, countryCode, campaignId, userId }) => {
    const request = this.prepareRequest(
      `${CONFIG.PAYMENT_BASE_URL}/capture-payment`,
      'POST',
      {
        paymentIntent,
        countryCode,
        campaignId,
        userId,
      },
      {},
      {},
      false,
    )
    return fetch(request)
  }

  refundPayment = ({ paymentIntent, countryCode, campaignId, userId }) => {
    if (!paymentIntent) return
    const request = this.prepareRequest(
      `${CONFIG.PAYMENT_BASE_URL}/refund`,
      'POST',
      {
        paymentIntent,
        countryCode,
        campaignId,
        userId,
      },
      {},
      {},
      false,
    )
    return fetch(request)
  }

  cancelPayment = ({ paymentIntent, countryCode, campaignId, userId }) => {
    if (!paymentIntent) return
    const request = this.prepareRequest(
      `${CONFIG.PAYMENT_BASE_URL}/cancel-payment`,
      'POST',
      {
        paymentIntent,
        countryCode,
        campaignId,
        userId,
      },
      {},
      {},
      false,
    )
    return fetch(request)
  }

  prepareRequest(
    url,
    method = 'GET',
    body = null,
    headers = {},
    fetchOptions = {},
    withCookies = true,
  ) {
    const completeHeaders = new Headers({
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      /* TODO: simple temporary solution - should be verified before request */
      'x-api-key': authorization.getAPIKey(),
      ...headers,
    })

    const initParam = {
      method,
      headers: completeHeaders,
      ...fetchOptions,
    }

    initParam['credentials'] = withCookies ? 'include' : 'omit'

    if (body) {
      initParam['body'] = JSON.stringify(body)
    }

    return new Request(url, initParam)
  }
}
export const apiClient = new APIClient()

export const sleep = ms => {
  return new Promise(resolve => setTimeout(resolve, ms))
}
export const fetchRetry = (request, delay, tries) => {
  function onError(err) {
    let triesLeft = tries - 1
    if (!triesLeft) {
      throw err
    }
    return sleep(delay).then(() => fetchRetry(request, delay, triesLeft))
  }
  // A Request body is a one-shot stream: the first fetch() consumes it, so
  // retrying with the same Request throws "Cannot construct a Request with a
  // Request object that has already been used" — which means retries never
  // actually worked and every transient failure surfaced as a hard error
  // (e.g. "Overlay saving failed" on the map). Fetch a fresh clone each
  // attempt and leave the original untouched so it stays clonable next retry.
  const toFetch =
    request && typeof request.clone === 'function' ? request.clone() : request
  return fetch(toFetch).catch(onError)
}
